import * as Yup from 'yup';

import { getMaxStringSchema } from 'src/compass/shared/schemas';

// Direct path, not the application barrel: this file is inside it, and importing it from itself
// is a cycle (see CasPendingEmailsPill in CasContextProviders).
import { SendChannelType, hasNothingToSend } from '../composeMode';
import { messageCategorySchema } from '../messageCategory';
import {
  attachmentCountProblem,
  dataObjectCount,
  getMaxBodySchema,
  imageBudgetProblem,
  imageSources,
  sourceBytes,
} from '../inlineImages';

// How the batch's own text body is named in the rewrite notice, where it sits beside row names.
export const DEFAULT_BODY_LABEL = 'Default message';

// msg.History.Subject is nvarchar(255). Batch Send's Subject boxes stop typing and pasting there;
// the schema below still names a longer value that arrives some other way.
export const MAX_SUBJECT_LENGTH = 255;

const SUBJECT_TOO_LONG = `Subject must be at most ${MAX_SUBJECT_LENGTH} characters`;

// The batch's own message is authored as one or the other; there is no Auto here, because the
// Default message has no recipients of its own to resolve a channel from.
export const BATCH_CHANNEL_OPTIONS: { id: SendChannelType; name: string }[] = [
  { id: 'email', name: 'Email' },
  { id: 'sms', name: 'SMS' },
];

/*
  The Default message's channel as a row's defaults are fetched with it (firstChannel), in the words
  the endpoints use for a pinned channel. It decides where a contact with no Preferred Channel is
  reached: on Email their email is tried first, then their mobile, then their phone; on SMS the
  mobile, the phone, then the email.
*/
export const FIRST_CHANNEL_PARAM: Record<SendChannelType, string> = { email: 'Email', sms: 'Text' };

/*
  A row's default recipients counted for the channel the Default message is on - what a row nobody
  has opened is judged on. The batch list endpoints return both pairs, emailFirst and phoneFirst, so
  switching the Default message needs no refetch.
*/
export const defaultCountsFor = (
  row: { emailFirst?: { email: number; phone: number }; phoneFirst?: { email: number; phone: number } },
  batchChannel: SendChannelType
) => {
  const counts = (batchChannel === 'sms' ? row.phoneFirst : row.emailFirst) ?? { email: 0, phone: 0 };

  return { emailContactCount: counts.email, phoneContactCount: counts.phone };
};

export const sameAddresses = (a: any[] = [], b: any[] = []) =>
  a.length === b.length && a.every((item, index) => String(item?.id) === String(b[index]?.id));

// Recipients count as customized only once they differ from what was loaded, so merely opening a
// row does not mark it Custom or start shipping addresses back to the server. Stated once: it is
// evaluated both live (from the form subscription) and on commit, against different snapshots.
export const isRecipientsEdited = (values: any, loaded?: { to: any[]; cc: any[] }) =>
  !!loaded && (!sameAddresses(values.to, loaded.to) || !sameAddresses(values.cc, loaded.cc));

/*
  Filter params as query-string pairs, for the replayable URL recorded on the batch. A nested value
  (the date-range filters' {start, end}) is written in bracket form - endDate[start]=... - the way the
  list request binds it, rather than String()-ed into "[object Object]". Dates (Date or moment) are
  written as ISO strings.
*/
export const toQueryPairs = (params: Record<string, any>): [string, string][] =>
  Object.entries(params ?? {}).flatMap(([key, value]): [string, string][] => {
    if (value === null || value === undefined || value === '') return [];
    if (typeof value?.toISOString === 'function') return [[key, value.toISOString()]];
    if (typeof value === 'object') {
      // "start" -> "endDate[start]"; a deeper "a[b]" -> "key[a][b]".
      return toQueryPairs(value).map(([subKey, subValue]): [string, string] => [
        `${key}${subKey.replace(/^([^[]+)/, '[$1]')}`,
        subValue,
      ]);
    }
    return [[key, String(value)]];
  });

export const withoutKey = <T,>(map: Record<string, T>, key: string): Record<string, T> =>
  Object.fromEntries(Object.entries(map).filter(([k]) => k !== key));

/*
  The messages in a batch with more pictures than their channel can carry - or, for a text, more
  pictures and tables than one MMS: it attaches a JPEG per SCE table its body names, then each
  picture, so a text can be over that count with no pictures at all.

  Each is judged on the rich body it will be sent from - a text's too, since its pictures are
  attached from there, like an SCE text's tables. That holds for every picture a text carries: one
  pasted into, or left off, a text is carried back into its rich body before anything is sent. A
  message still on the default body is named once, as the default message, however many rows use
  it. The server refuses the same batch (InlineImages.EnsureBatchFits); this names the messages
  before anything is sent.
*/
export function findOversizedMessages(
  messages: {
    label: string;
    ownBody?: string;
    channel: SendChannelType;
  }[],
  defaultBody: string
): { label: string; problem: string }[] {
  // Read once: a batch can have hundreds of rows on the same default body.
  const defaultSources = imageSources(defaultBody);
  const defaultTables = dataObjectCount(defaultBody);
  const found: { label: string; problem: string }[] = [];

  messages.forEach(({ label, ownBody, channel }) => {
    const usesDefault = ownBody === undefined;
    const sources = usesDefault ? defaultSources : imageSources(ownBody);
    const tables = usesDefault ? defaultTables : dataObjectCount(ownBody);
    const name = usesDefault ? DEFAULT_BODY_LABEL : label;

    const problems = [
      imageBudgetProblem(sourceBytes(sources), channel),
      channel === 'sms' ? attachmentCountProblem(tables + sources.length) : null,
    ];

    problems.forEach((problem) => {
      if (problem && !found.some((f) => f.label === name && f.problem === problem)) {
        found.push({ label: name, problem });
      }
    });
  });

  return found;
}

// An SCE data object in a rich body, as the editor serializes it for sending (prepareHtmlForEmail).
const DATA_OBJECT = /<dataobject\b/gi;

// What a text sent from this rich body attaches: each data table, as a picture of its rows, and
// each pasted picture.
const attachmentCount = (body?: string | null) =>
  ((body ?? '').match(DATA_OBJECT)?.length ?? 0) + imageSources(body).length;

/*
  The texts in a batch with neither words nor anything attached, which the server refuses
  (SmsService.EnsureEveryTextHasContent) - see hasNothingToSend for the rule.

  Each is judged on its own words and on the rich body it is sent from, which is where its tables
  and pictures are attached from: the default message's text and body for every text row on the
  default message, named once however many rows use it, and a customized row's own text and body -
  its own even when it has no words, since that is how the server sends it.
*/
export function findEmptyTexts(
  defaultText: { text: string; body: string } | null,
  rowTexts: { label: string; text: string; body: string }[]
): string[] {
  return [...(defaultText ? [{ label: DEFAULT_BODY_LABEL, ...defaultText }] : []), ...rowTexts]
    .filter(({ text, body }) => hasNothingToSend(text, attachmentCount(body)))
    .map(({ label }) => label);
}

/*
  What a Batch Send's own form will not accept.

  Everything here is about the batch's authored message and the open row's override. The
  cross-row rules - is a Subject needed, is a text body needed, which rows cannot resolve a
  channel - are checked on Send instead: they depend on the row maps beside this form, which a
  schema never sees.
*/
export const batchComposeSchema = Yup.object().shape({
  // One for the whole batch, picked above the Default message - see RHFMessageCategory.
  category: messageCategorySchema,
  // Not required here: only an email has a subject, and which rows are emails lives beside this
  // form - so Send checks it, and brings the field on screen when it is missing.
  subject: Yup.string().max(MAX_SUBJECT_LENGTH, SUBJECT_TOO_LONG).nullable(),
  // Both rich bodies are counted as sent, a picture as its reference - see bodyLength.
  content: getMaxBodySchema().required('Email body is required.'),
  // The per-row override fields. Length-capped like the batch's own (the server enforces the same
  // caps on every message), but not required: they are only meaningful while a row has "use default
  // message" unchecked, and a required rule would fire on fields the user cannot even see.
  rowSubject: Yup.string().max(MAX_SUBJECT_LENGTH, SUBJECT_TOO_LONG).nullable(),
  rowContent: getMaxBodySchema(),
  // Not required here for the same reason as the row fields: whether a text body is needed depends
  // on the rows' channels, which live beside this form rather than in it, so it is checked at send
  // time along with the other cross-row rules.
  textContent: getMaxStringSchema(),
});

/*
  The fields Send validates through the form: the Default message's own. The row fields are left
  out because they hold whichever row was last put into the row editor - by Send, perhaps one that
  has gone back to the default message, whose leftover Body would block every Send in silence.
  The rows' own messages are checked as they will be sent instead - see findTooLongRow.
*/
export const DEFAULT_MESSAGE_FIELDS = ['category', 'subject', 'content', 'textContent'];

const rowMessageSchema = batchComposeSchema.pick(['rowSubject', 'rowContent']);

/*
  The first of these rows whose own message is longer than the row editor allows, or undefined.

  Judged on the overrides commitCurrent returns - the messages that will be sent - by the row
  editor's own rules. A row on the default message has nothing of its own to judge.
*/
export const findTooLongRow = (
  keys: string[],
  overrides: Record<string, { subject: string; content: string }>
) =>
  keys.find(
    (key) =>
      !!overrides[key] &&
      !rowMessageSchema.isValidSync({
        rowSubject: overrides[key].subject,
        rowContent: overrides[key].content,
      })
  );
